import { DayProps } from '@unistocks/components/calendar/day/day';
import { today } from '@unistocks/shared/utils/util';
import type { DateStringValue } from '../../types';
import { isAfterMinDate } from '../is-after-min-date/is-after-min-date';
import { isBeforeMaxDate } from '../is-before-max-date/is-before-max-date';
import { isSameMonth } from '../is-same-month/is-same-month';

interface GetDateInTabOrderInput {
  /** The month grid as weeks of `yyyy-MM-dd` strings (from `getMonthDays`). */
  dates: DateStringValue[][];
  /** Lower bound, `yyyy-MM-dd`. */
  minDate: DateStringValue | undefined;
  /** Upper bound, `yyyy-MM-dd`. */
  maxDate: DateStringValue | undefined;
  /**
   * Per-day prop getter — its `disabled`/`selected` participate in the choice.
   *
   * It is consulted TWICE per date below (once for `disabled`, once for
   * `selected`), so callers should pass a getter wrapped in
   * `memoizeByDate` — the same wrapper they use for their own cell loop, so the
   * whole render resolves each date's props exactly once. See
   * `internal/memoize-control-props.ts`.
   */
  getDayProps: ((date: DateStringValue) => Partial<DayProps>) | undefined;
  /** Predicate marking a day disabled. */
  excludeDate: ((date: DateStringValue) => boolean) | undefined;
  /** Whether outside-month days are hidden (excluded from candidacy). */
  hideOutsideDates: boolean | undefined;
  /** The displayed month, `yyyy-MM-dd`. */
  month: DateStringValue;
}

/**
 * Picks the single date that should carry `tabIndex={0}` (roving tabindex):
 * the selected enabled day, else today, else the first enabled day. Mirrors
 * @mantine/dates `getDateInTabOrder`. Returns `undefined` when no day qualifies.
 */
export function getDateInTabOrder({
  dates,
  minDate,
  maxDate,
  getDayProps,
  excludeDate,
  hideOutsideDates,
  month
}: GetDateInTabOrderInput): DateStringValue | undefined {
  const enabledDates = dates
    .flat()
    .filter(
      date =>
        isBeforeMaxDate(date, maxDate) &&
        isAfterMinDate(date, minDate) &&
        !excludeDate?.(date) &&
        !getDayProps?.(date)?.disabled &&
        (!hideOutsideDates || isSameMonth(date, month))
    );

  const selectedDate = enabledDates.find(date => getDayProps?.(date)?.selected);

  if (selectedDate) {
    return selectedDate;
  }

  // `dayjs().isSame(date, "date")` inside the predicate built a FRESH "now"
  // instance (plus dayjs' internal `startOf`/`endOf` clones) for every candidate
  // — ~4 allocations × up to 42 dates, to answer a question whose left-hand side
  // never changes. One formatted "today" string compares exactly instead:
  // `dates` always comes from `getMonthDays`, which emits zero-padded
  // `yyyy-MM-dd`, so string equality is equivalent to a day-granularity `isSame`.
  const todayDate = today().toISODate();
  const currentDate = enabledDates.find(date => date.slice(0, 10) === todayDate);

  if (currentDate) {
    return currentDate;
  }

  return enabledDates[0];
}
